'use client';

import { useState } from 'react';
import Image from 'next/image';

import { Button, Input, Modal } from '@shared/ui/components';
import ArrowIcon from '@public/icons/arrow-right.svg';
import CheckIcon from '@public/icons/check-extra-thin.svg';

import { contactUsApi } from '../../api/contact.api';

import styles from './ContactUsModal.module.scss';

interface ContactUsModalProps {
  isOpen: boolean;
  close: () => void;
}

const ContactUsModal = (props: ContactUsModalProps) => {
  const { isOpen, close } = props;

  const [firstName, setFirstName] = useState<string>('');
  const [lastName, setLastName] = useState<string>('');
  const [email, setEmail] = useState<string>('');
  const [message, setMessage] = useState<string>('');
  const [isMessageSent, setMessageSent] = useState<boolean>(false);
  const [isLoading, setLoading] = useState<boolean>(false);

  const isFormValid: boolean = (() => {
    return !!firstName && !!lastName && !!email && !!message;
  })();

  const sendMessage = () => {
    if (isLoading || !isFormValid) {
      return;
    }

    setLoading(true);

    contactUsApi({
      first_name: firstName,
      last_name: lastName,
      email,
      message,
    })
      .then(() => {
        setMessageSent(true);
        setMessage('');
      })
      .finally(() => {
        setLoading(false);
      });
  };

  return (
    <Modal
      isOpen={isOpen}
      header={isMessageSent ? ' ' : 'Contact us'}
      className={styles.modal}
      bodyClassName={styles.modal_body}
      headerClassName={styles.modal_header}
      separateClose
      close={close}
      onClose={() => {
        setMessageSent(false);
      }}
    >
      {isMessageSent ? (
        <div className={styles.sent}>
          <div className={styles.sent_icon}>
            <Image src={CheckIcon} alt="Check" draggable={false} />
          </div>

          <div className={styles.sent_text}>Your message has been sent successfully</div>

          <Button size="l" variant="filled" className={styles.sent_button} onClick={close}>
            OK
          </Button>
        </div>
      ) : (
        <>
          <div className={styles.form}>
            <div className={styles.form_top}>
              <Input placeholder="First name" value={firstName} onChange={setFirstName} />

              <Input placeholder="Last name" value={lastName} onChange={setLastName} />

              <Input placeholder="Email" value={email} onChange={setEmail} />
            </div>

            <Input
              placeholder="Message"
              lines={1}
              value={message}
              className={styles.textarea}
              onChange={setMessage}
            />
          </div>

          <Button
            size="l"
            variant="filled"
            className={styles.button}
            loading={isLoading}
            disabled={!isFormValid}
            onClick={sendMessage}
          >
            <div>Send</div>
            <Image src={ArrowIcon} alt="Arrow" draggable={false} />
          </Button>
        </>
      )}
    </Modal>
  );
};

export default ContactUsModal;
